import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { XMarkIcon, PencilIcon, TrashIcon, Bars3Icon, PlusIcon } from '@heroicons/react/24/outline';
import {
  parseMarkdownToBlocks,
  getBlockTitle,
  getBlockPreview,
  parseLayout,
  serializeLayout,
  applyLayout,
  detectChartVersion,
  parseChartV2,
  serializeChartV2,
  v2BlocksToEditFormat,
  blocksToV2Format,
  applyV2Layout,
  updateV2Layout,
  generateBlockId
} from '../utils/chartParser';
import { migrateChartV1toV2 } from '../utils/chartMigration';

const ChartViewModal = ({ isOpen, onClose, chart, chartLayout, songTitle, onSave, onDelete, onSaveLayout, onConvertToV2 }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isLayoutMode, setIsLayoutMode] = useState(false);
  const [editedChart, setEditedChart] = useState('');
  const [columns, setColumns] = useState([]);
  const [draggedBlock, setDraggedBlock] = useState(null);
  const [dragOverColumn, setDragOverColumn] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  // Edit mode drag state
  const [draggedEditBlock, setDraggedEditBlock] = useState(null);
  const [editDragOverIndex, setEditDragOverIndex] = useState(null);

  // V2 specific state
  const [editBlocks, setEditBlocks] = useState([]);
  const [v2Layout, setV2Layout] = useState({ portrait: { columns: [] }, landscape: { columns: [] } });
  const [activeBlockId, setActiveBlockId] = useState(null); // For click-to-edit

  // Ref for click-outside detection
  const editorContainerRef = useRef(null);

  // Detect chart version
  const chartVersion = useMemo(() => detectChartVersion(chart), [chart]);

  // Parse blocks based on version
  const blocks = useMemo(() => {
    if (chartVersion === 2) {
      const parsed = parseChartV2(chart);
      return parsed.blocks;
    }
    return parseMarkdownToBlocks(chart);
  }, [chart, chartVersion]);

  // Check if landscape
  const isLandscape = typeof window !== 'undefined' && window.innerWidth > window.innerHeight;
  const numColumns = isLandscape ? 3 : 2;

  // Create a stable block lookup map for V2
  const blocksMap = useMemo(() => {
    if (chartVersion === 2) {
      return new Map(blocks.map(b => [b.id, b]));
    }
    return new Map();
  }, [blocks, chartVersion]);

  // Compute unassigned blocks (blocks that exist but aren't in any column)
  const unassignedBlockIds = useMemo(() => {
    if (chartVersion !== 2 || !isLayoutMode) return [];
    const assignedIds = new Set(columns.flat());
    return blocks.map(b => b.id).filter(id => !assignedIds.has(id));
  }, [chartVersion, isLayoutMode, columns, blocks]);

  // Initialize columns from layout or auto-distribute
  // For V2, columns store block IDs (strings), not block objects
  useEffect(() => {
    if (chartVersion === 2) {
      const parsed = parseChartV2(chart);
      setV2Layout(parsed.layout || { portrait: { columns: [] }, landscape: { columns: [] } });
      // applyV2Layout returns block objects, convert to IDs for state
      const blockColumns = applyV2Layout(parsed.blocks, parsed.layout, isLandscape);
      const idColumns = blockColumns.map(col => col.map(block => block.id));
      setColumns(idColumns);
    } else {
      const layout = parseLayout(chartLayout);
      const newColumns = applyLayout(blocks, layout, isLandscape);
      setColumns(newColumns);
    }
  }, [chart, chartLayout, blocks, isLandscape, chartVersion]);

  useEffect(() => {
    if (chartVersion === 2) {
      const parsed = parseChartV2(chart);
      setEditBlocks(v2BlocksToEditFormat(parsed.blocks));
    } else {
      setEditedChart(chart || '');
    }
    setIsEditing(false);
    setIsLayoutMode(false);
    setActiveBlockId(null);
  }, [chart, chartVersion]);

  // Handle click outside to exit edit mode for a block
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (activeBlockId && editorContainerRef.current) {
        const activeBlockElement = editorContainerRef.current.querySelector(`[data-block-id="${activeBlockId}"]`);
        if (activeBlockElement && !activeBlockElement.contains(e.target)) {
          setActiveBlockId(null);
        }
      }
    };

    if (isEditing && chartVersion === 2) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [activeBlockId, isEditing, chartVersion]);

  // Handle Escape key to exit edit mode
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && activeBlockId) {
        setActiveBlockId(null);
      }
    };

    if (isEditing && chartVersion === 2) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [activeBlockId, isEditing, chartVersion]);

  const handleSave = () => {
    if (chartVersion === 2) {
      // Convert edit blocks to V2 format and serialize
      const { blocks: newBlocks, layout: newLayout } = blocksToV2Format(editBlocks);
      // Preserve existing layout where possible
      const finalLayout = {
        portrait: v2Layout.portrait?.columns?.length ? v2Layout.portrait : newLayout.portrait,
        landscape: v2Layout.landscape?.columns?.length ? v2Layout.landscape : newLayout.landscape
      };
      const v2Json = serializeChartV2(newBlocks, finalLayout);
      onSave(v2Json);
    } else {
      onSave(editedChart);
    }
    setIsEditing(false);
    setActiveBlockId(null);
    onClose();
  };

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this chart?')) {
      onDelete();
      onClose();
    }
  };

  const handleEdit = () => {
    if (chartVersion === 2) {
      const parsed = parseChartV2(chart);
      setEditBlocks(v2BlocksToEditFormat(parsed.blocks));
    } else {
      setEditedChart(chart);
    }
    setIsEditing(true);
    setIsLayoutMode(false);
  };

  const handleEnterLayoutMode = () => {
    setIsLayoutMode(true);
    setIsEditing(false);
  };

  const handleSaveLayout = () => {
    if (chartVersion === 2) {
      // For V2, columns already contains IDs, create layout directly
      const parsed = parseChartV2(chart);
      const orientation = isLandscape ? 'landscape' : 'portrait';
      const newLayout = {
        ...v2Layout,
        [orientation]: { columns: columns }
      };
      setV2Layout(newLayout);
      const v2Json = serializeChartV2(parsed.blocks, newLayout);
      onSave(v2Json);
    } else {
      const layoutData = {
        [isLandscape ? 'landscape' : 'portrait']: { columns }
      };

      // Preserve the other orientation's layout if it exists
      const existingLayout = parseLayout(chartLayout);
      if (existingLayout) {
        const otherOrientation = isLandscape ? 'portrait' : 'landscape';
        if (existingLayout[otherOrientation]) {
          layoutData[otherOrientation] = existingLayout[otherOrientation];
        }
      }

      const layoutJson = serializeLayout(layoutData);
      if (onSaveLayout) {
        onSaveLayout(layoutJson);
      }
    }
    setIsLayoutMode(false);
  };

  const handleCancelLayout = () => {
    // Restore columns from saved layout
    if (chartVersion === 2) {
      const parsed = parseChartV2(chart);
      const blockColumns = applyV2Layout(parsed.blocks, parsed.layout, isLandscape);
      // Convert to IDs for state
      const idColumns = blockColumns.map(col => col.map(block => block.id));
      setColumns(idColumns);
    } else {
      const layout = parseLayout(chartLayout);
      const restoredColumns = applyLayout(blocks, layout, isLandscape);
      setColumns(restoredColumns);
    }
    setIsLayoutMode(false);
  };

  const handleConvertToV2 = () => {
    if (window.confirm('Convert this chart to the new V2 format? This enables the block-based editor.')) {
      const v2Data = migrateChartV1toV2(chart, chartLayout);
      const v2Json = serializeChartV2(v2Data.blocks, v2Data.layout);
      if (onConvertToV2) {
        onConvertToV2(v2Json);
      } else {
        onSave(v2Json);
      }
    }
  };

  // V2 Block Editor handlers
  const handleAddBlock = () => {
    const newBlock = {
      id: generateBlockId(),
      header: 'New Section',
      content: ''
    };
    setEditBlocks([...editBlocks, newBlock]);
    setActiveBlockId(newBlock.id);
  };

  const handleUpdateBlock = (blockId, field, value) => {
    setEditBlocks(editBlocks.map(block =>
      block.id === blockId ? { ...block, [field]: value } : block
    ));
  };

  const handleDeleteBlock = (blockId) => {
    if (editBlocks.length <= 1) {
      alert('Cannot delete the last block. A chart must have at least one block.');
      return;
    }
    setEditBlocks(editBlocks.filter(block => block.id !== blockId));
    if (activeBlockId === blockId) {
      setActiveBlockId(null);
    }
  };

  // Edit mode drag handlers for reordering blocks
  const handleEditDragStart = useCallback((e, blockId, fromIndex) => {
    setDraggedEditBlock({ blockId, fromIndex });
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', '');
  }, []);

  const handleEditDragOver = useCallback((e, toIndex) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setEditDragOverIndex(toIndex);
  }, []);

  const handleEditDragLeave = useCallback(() => {
    setEditDragOverIndex(null);
  }, []);

  const handleEditDrop = useCallback((e, toIndex) => {
    e.preventDefault();
    if (!draggedEditBlock) return;

    const { fromIndex } = draggedEditBlock;
    if (fromIndex === toIndex || fromIndex === toIndex - 1) {
      // No move needed
      setDraggedEditBlock(null);
      setEditDragOverIndex(null);
      return;
    }

    setEditBlocks(prevBlocks => {
      const newBlocks = [...prevBlocks];
      const [removed] = newBlocks.splice(fromIndex, 1);
      const adjustedIndex = fromIndex < toIndex ? toIndex - 1 : toIndex;
      newBlocks.splice(adjustedIndex, 0, removed);
      return newBlocks;
    });

    setDraggedEditBlock(null);
    setEditDragOverIndex(null);
  }, [draggedEditBlock]);

  const handleEditDragEnd = useCallback(() => {
    setDraggedEditBlock(null);
    setEditDragOverIndex(null);
  }, []);

  // Drag and drop handlers for layout mode
  // blockId is the ID (string for V2) or index (number for V1)
  const handleDragStart = useCallback((e, blockId, fromColumn) => {
    setDraggedBlock({ blockId, fromColumn });
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', '');
  }, []);

  const handleDragOver = useCallback((e, toColumn, toIndex) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverColumn(toColumn);
    setDragOverIndex(toIndex);
  }, []);

  const handleDragLeave = useCallback(() => {
    setDragOverColumn(null);
    setDragOverIndex(null);
  }, []);

  const handleDrop = useCallback((e, toColumn, toIndex) => {
    e.preventDefault();

    if (!draggedBlock) return;

    const { blockId, fromColumn } = draggedBlock;

    setColumns(prevColumns => {
      const newColumns = prevColumns.map(col => [...col]);
      let adjustedToIndex = toIndex;

      // For V2, columns now store IDs (strings)
      // For V1, columns store indices (numbers)
      // Both cases can use indexOf since we're comparing primitives

      // Find and remove from ALL columns (prevents duplicates)
      for (let i = 0; i < newColumns.length; i++) {
        const idx = newColumns[i].indexOf(blockId);
        if (idx !== -1) {
          newColumns[i].splice(idx, 1);
          // Adjust target index if removing from same column before target position
          if (i === toColumn && idx < adjustedToIndex) {
            adjustedToIndex--;
          }
        }
      }

      // Insert at new position (only if not already there)
      if (!newColumns[toColumn].includes(blockId)) {
        newColumns[toColumn].splice(adjustedToIndex, 0, blockId);
      }

      return newColumns;
    });

    setDraggedBlock(null);
    setDragOverColumn(null);
    setDragOverIndex(null);
  }, [draggedBlock]);

  const handleDragEnd = useCallback(() => {
    setDraggedBlock(null);
    setDragOverColumn(null);
    setDragOverIndex(null);
  }, []);

  // Render a single V2 block for view mode (read-only display)
  const renderV2Block = useCallback((block, index) => {
    return (
      <div key={block.id || index} className="mb-4">
        <h2 className="text-xl font-semibold mb-2">{block.header}</h2>
        {block.content && (
          <pre className="font-mono text-sm whitespace-pre-wrap bg-gray-50 p-3 rounded">
            {block.content}
          </pre>
        )}
      </div>
    );
  }, []);

  // Render a single V2 block for edit mode (click-to-edit inline)
  const renderV2EditBlock = useCallback((block, index) => {
    const isActive = activeBlockId === block.id;
    const isDragging = draggedEditBlock && draggedEditBlock.blockId === block.id;

    if (isActive) {
      // Edit mode for this block
      return (
        <div
          key={block.id}
          data-block-id={block.id}
          className="mb-2 border-2 border-blue-400 rounded-lg p-4 bg-blue-50"
        >
          <div className="flex items-center justify-between mb-3">
            <div
              draggable
              onDragStart={(e) => handleEditDragStart(e, block.id, index)}
              onDragEnd={handleEditDragEnd}
              className="cursor-move p-1 mr-2 text-gray-400 hover:text-gray-600"
              title="Drag to reorder"
            >
              <Bars3Icon className="w-5 h-5" />
            </div>
            <input
              type="text"
              value={block.header}
              onChange={(e) => handleUpdateBlock(block.id, 'header', e.target.value)}
              className="text-xl font-semibold bg-white border border-gray-300 rounded px-2 py-1 flex-1 mr-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Section header..."
              autoFocus
            />
            <button
              onClick={() => handleDeleteBlock(block.id)}
              className="p-2 text-red-500 hover:text-red-700 hover:bg-red-100 rounded"
              title="Delete section"
            >
              <TrashIcon className="w-5 h-5" />
            </button>
          </div>
          <textarea
            value={block.content || ''}
            onChange={(e) => handleUpdateBlock(block.id, 'content', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 font-mono text-sm resize-none bg-white"
            rows={6}
            placeholder="Chords and lyrics..."
          />
        </div>
      );
    }

    // View mode for this block (click to edit, draggable)
    return (
      <div
        key={block.id}
        data-block-id={block.id}
        className={`mb-2 border rounded-lg p-4 transition-all ${
          isDragging
            ? 'opacity-50 border-blue-400 bg-blue-50'
            : 'border-transparent hover:border-gray-300 hover:bg-gray-50'
        }`}
      >
        <div className="flex items-start">
          <div
            draggable
            onDragStart={(e) => handleEditDragStart(e, block.id, index)}
            onDragEnd={handleEditDragEnd}
            className="cursor-move p-1 mr-2 text-gray-400 hover:text-gray-600 flex-shrink-0"
            title="Drag to reorder"
          >
            <Bars3Icon className="w-5 h-5" />
          </div>
          <div
            onClick={() => setActiveBlockId(block.id)}
            className="flex-1 cursor-pointer"
          >
            <h2 className="text-xl font-semibold mb-2">{block.header || 'Section'}</h2>
            {block.content ? (
              <pre className="font-mono text-sm whitespace-pre-wrap bg-gray-50 p-3 rounded">
                {block.content}
              </pre>
            ) : (
              <p className="text-gray-400 italic text-sm">Click to add content...</p>
            )}
          </div>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteBlock(block.id);
            }}
            className="p-1 ml-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded flex-shrink-0"
            title="Delete section"
          >
            <TrashIcon className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }, [activeBlockId, draggedEditBlock, handleUpdateBlock, handleDeleteBlock, handleEditDragStart, handleEditDragEnd]);

  // Render a single V1 block for view mode (existing logic)
  const renderBlock = useCallback((block, index) => {
    const processInlineCode = (text) => {
      const parts = text.split('`');
      return parts.map((part, i) => {
        if (i % 2 === 1) {
          return <code key={i} className="bg-gray-100 px-1 rounded text-sm font-mono">{part}</code>;
        }
        return part;
      });
    };

    const headerElement = block.header && (
      block.header.type === 'h1' ? <h1 className="text-2xl font-bold mb-2">{block.header.title}</h1> :
      block.header.type === 'h2' ? <h2 className="text-xl font-semibold mb-2">{block.header.title}</h2> :
      <h3 className="text-lg font-medium mb-1">{block.header.title}</h3>
    );

    let contentElement;
    switch (block.type) {
      case 'h1':
        contentElement = <h1 className="text-2xl font-bold mb-4">{block.content}</h1>;
        break;
      case 'h2':
        contentElement = <h2 className="text-xl font-semibold mb-3">{block.content}</h2>;
        break;
      case 'h3':
        contentElement = <h3 className="text-lg font-medium mb-2">{block.content}</h3>;
        break;
      case 'code':
        contentElement = (
          <pre className="bg-gray-100 p-3 rounded text-sm font-mono overflow-x-auto mb-4">
            <code>{block.content}</code>
          </pre>
        );
        break;
      case 'pre':
        contentElement = (
          <pre className="bg-gray-100 p-3 rounded text-sm font-mono overflow-x-auto mb-2">
            <code>{block.content}</code>
          </pre>
        );
        break;
      default:
        contentElement = (
          <p className="mb-2 font-mono text-sm whitespace-pre-wrap">
            {processInlineCode(block.content)}
          </p>
        );
    }

    return (
      <div key={index}>
        {headerElement}
        {contentElement}
      </div>
    );
  }, []);

  // Handle removing a block from layout (moves it out of all columns)
  // blockId is the ID (string for V2) or index (number for V1)
  const handleRemoveFromLayout = useCallback((blockId, columnIndex) => {
    setColumns(prevColumns => {
      const newColumns = prevColumns.map(col => [...col]);
      const idx = newColumns[columnIndex].indexOf(blockId);
      if (idx !== -1) {
        newColumns[columnIndex].splice(idx, 1);
      }
      return newColumns;
    });
  }, []);

  // Render a draggable block in layout mode
  // blockId is the ID (string for V2) or index (number for V1)
  const renderDraggableBlock = useCallback((blockId, columnIndex, positionIndex) => {
    if (blockId === null || blockId === undefined) return null;

    const isV2 = chartVersion === 2;
    // Look up the block from blocksMap (V2) or blocks array (V1)
    const block = isV2 ? blocksMap.get(blockId) : blocks[blockId];
    if (!block) return null;

    const isDragging = draggedBlock && draggedBlock.blockId === blockId;

    const title = isV2
      ? (block.header || 'Section')
      : getBlockTitle(block, blockId);
    const preview = isV2
      ? (block.content || '').replace(/\n/g, ' ').substring(0, 60)
      : getBlockPreview(block, 60);

    return (
      <div key={blockId}>
        <div
          draggable
          onDragStart={(e) => {
            e.stopPropagation();
            handleDragStart(e, blockId, columnIndex);
          }}
          onDragEnd={handleDragEnd}
          onDragOver={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleDragOver(e, columnIndex, positionIndex);
          }}
          onDragLeave={(e) => {
            e.stopPropagation();
            handleDragLeave();
          }}
          onDrop={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleDrop(e, columnIndex, positionIndex);
          }}
          className={`border-2 border-dashed rounded-lg p-3 mb-2 cursor-move transition-all ${
            isDragging
              ? 'opacity-50 border-blue-400 bg-blue-50'
              : 'border-gray-300 bg-white hover:border-blue-400 hover:bg-blue-50'
          }`}
        >
          <div className="flex items-start space-x-2">
            <Bars3Icon className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="font-medium text-sm text-gray-900 truncate">
                {title}
              </div>
              <div className="text-xs text-gray-500 truncate mt-1">
                {preview}
              </div>
            </div>
            {columnIndex >= 0 && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemoveFromLayout(blockId, columnIndex);
                }}
                className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded flex-shrink-0"
                title="Remove from layout"
              >
                <TrashIcon className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }, [blocks, blocksMap, chartVersion, draggedBlock, handleDragStart, handleDragEnd, handleDragOver, handleDragLeave, handleDrop, handleRemoveFromLayout]);

  // Render column in normal view mode
  // For V2, columnBlocks contains IDs; for V1, it contains indices
  const renderColumn = useCallback((columnBlocks) => {
    if (chartVersion === 2) {
      return columnBlocks.map((blockId, idx) => {
        const block = blocksMap.get(blockId);
        if (!block) return null;
        return renderV2Block(block, idx);
      });
    }
    return columnBlocks.map((blockIndex) => {
      const block = blocks[blockIndex];
      if (!block) return null;
      return renderBlock(block, blockIndex);
    });
  }, [blocks, blocksMap, chartVersion, renderBlock, renderV2Block]);

  // Render a drop zone between blocks
  const renderDropZone = useCallback((columnIndex, positionIndex) => {
    const isActive = dragOverColumn === columnIndex && dragOverIndex === positionIndex;
    return (
      <div
        key={`dropzone-${columnIndex}-${positionIndex}`}
        onDragOver={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleDragOver(e, columnIndex, positionIndex);
        }}
        onDragLeave={(e) => {
          e.stopPropagation();
          handleDragLeave();
        }}
        onDrop={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleDrop(e, columnIndex, positionIndex);
        }}
        className={`h-4 rounded transition-all ${isActive ? 'bg-blue-400' : 'bg-transparent hover:bg-blue-100'}`}
      />
    );
  }, [dragOverColumn, dragOverIndex, handleDragOver, handleDragLeave, handleDrop]);

  // Render column in layout mode
  const renderLayoutColumn = useCallback((columnIndex) => {
    const columnBlocks = columns[columnIndex] || [];

    return (
      <div
        className="flex-1 min-h-[200px] p-2 bg-gray-50 rounded-lg"
        onDragOver={(e) => {
          e.preventDefault();
          // Only set drop target to end if not over a specific zone
          if (e.target === e.currentTarget) {
            handleDragOver(e, columnIndex, columnBlocks.length);
          }
        }}
        onDragLeave={handleDragLeave}
        onDrop={(e) => {
          e.preventDefault();
          if (e.target === e.currentTarget) {
            handleDrop(e, columnIndex, columnBlocks.length);
          }
        }}
      >
        <div className="text-xs font-medium text-gray-500 mb-2 text-center">
          Column {columnIndex + 1}
        </div>
        {/* Drop zone at top of column */}
        {renderDropZone(columnIndex, 0)}
        {columnBlocks.map((blockId, positionIndex) => (
          <React.Fragment key={blockId}>
            {renderDraggableBlock(blockId, columnIndex, positionIndex)}
            {/* Drop zone after each block */}
            {renderDropZone(columnIndex, positionIndex + 1)}
          </React.Fragment>
        ))}
      </div>
    );
  }, [columns, handleDragOver, handleDragLeave, handleDrop, renderDraggableBlock, renderDropZone]);

  // Render edit mode drop zone between blocks
  const renderEditDropZone = useCallback((positionIndex) => {
    const isActive = editDragOverIndex === positionIndex;
    return (
      <div
        key={`edit-dropzone-${positionIndex}`}
        onDragOver={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleEditDragOver(e, positionIndex);
        }}
        onDragLeave={(e) => {
          e.stopPropagation();
          handleEditDragLeave();
        }}
        onDrop={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleEditDrop(e, positionIndex);
        }}
        className={`h-2 rounded transition-all ${isActive ? 'bg-blue-400 h-4' : 'bg-transparent hover:bg-blue-100'}`}
      />
    );
  }, [editDragOverIndex, handleEditDragOver, handleEditDragLeave, handleEditDrop]);

  // Render V2 block editor with click-to-edit
  const renderBlockEditor = () => {
    return (
      <div ref={editorContainerRef} className="flex flex-col h-full overflow-auto">
        <div className="pb-4">
          {/* Drop zone at top */}
          {renderEditDropZone(0)}
          {editBlocks.map((block, index) => (
            <React.Fragment key={block.id}>
              {renderV2EditBlock(block, index)}
              {/* Drop zone after each block */}
              {renderEditDropZone(index + 1)}
            </React.Fragment>
          ))}
        </div>

        <button
          onClick={handleAddBlock}
          className="flex items-center justify-center space-x-2 w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-blue-400 hover:text-blue-600 transition-colors"
        >
          <PlusIcon className="w-5 h-5" />
          <span>Add Section</span>
        </button>
      </div>
    );
  };

  if (!isOpen || !chart) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg w-full max-w-full mx-4 h-[95vh] flex flex-col">
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center space-x-3">
            <h3 className="text-lg font-semibold text-gray-900">
              Chart - {songTitle}
            </h3>
            <span className={`text-xs px-2 py-1 rounded ${chartVersion === 2 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
              V{chartVersion}
            </span>
            {isLayoutMode && <span className="text-sm font-normal text-blue-600">(Rearrange Mode)</span>}
            {isEditing && chartVersion === 2 && <span className="text-sm font-normal text-green-600">(Click a section to edit)</span>}
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        <div className="flex-1 p-6 overflow-hidden flex flex-col min-h-0">
          {isEditing ? (
            chartVersion === 2 ? (
              renderBlockEditor()
            ) : (
              <div className="flex flex-col h-full">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Chart (Markdown)
                </label>
                <textarea
                  value={editedChart}
                  onChange={(e) => setEditedChart(e.target.value)}
                  className="flex-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 font-mono text-sm resize-none"
                  placeholder="Enter your chart in markdown format..."
                />
              </div>
            )
          ) : (
            <div className="flex-1 overflow-auto">
              <div className={`flex gap-8 ${isLayoutMode ? '' : 'h-full'}`}>
                {columns.map((columnBlocks, columnIndex) => (
                  <React.Fragment key={columnIndex}>
                    {isLayoutMode ? (
                      renderLayoutColumn(columnIndex)
                    ) : (
                      <div className="flex-1 prose max-w-none">
                        {renderColumn(columnBlocks)}
                      </div>
                    )}
                    {columnIndex < columns.length - 1 && !isLayoutMode && (
                      <div className="w-px bg-gray-200" />
                    )}
                  </React.Fragment>
                ))}
              </div>
              {/* Unassigned blocks section for layout mode */}
              {isLayoutMode && unassignedBlockIds.length > 0 && (
                <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div className="text-sm font-medium text-yellow-800 mb-2">
                    Unassigned Blocks (drag to add)
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {unassignedBlockIds.map((blockId, idx) => (
                      <div key={blockId} className="w-64">
                        {renderDraggableBlock(blockId, -1, idx)}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex justify-between items-center p-6 border-t">
          <div>
            {isEditing && (
              <button
                onClick={handleDelete}
                className="px-4 py-2 text-red-600 bg-red-50 rounded-md hover:bg-red-100 flex items-center space-x-2"
              >
                <TrashIcon className="w-4 h-4" />
                <span>Delete Chart</span>
              </button>
            )}
          </div>
          <div className="flex space-x-3">
            {isLayoutMode ? (
              <>
                <button
                  onClick={handleCancelLayout}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveLayout}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Save Layout
                </button>
              </>
            ) : isEditing ? (
              <>
                <button
                  onClick={() => {
                    setIsEditing(false);
                    setActiveBlockId(null);
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Save Chart
                </button>
              </>
            ) : (
              <>
                {chartVersion === 1 && (
                  <button
                    onClick={handleConvertToV2}
                    className="px-4 py-2 bg-purple-100 text-purple-700 rounded-md hover:bg-purple-200"
                  >
                    Convert to V2
                  </button>
                )}
                <button
                  onClick={handleEnterLayoutMode}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 flex items-center space-x-2"
                >
                  <Bars3Icon className="w-4 h-4" />
                  <span>Rearrange</span>
                </button>
                <button
                  onClick={handleEdit}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center space-x-2"
                >
                  <PencilIcon className="w-4 h-4" />
                  <span>Edit Chart</span>
                </button>
                <button
                  onClick={onClose}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
                >
                  Close
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChartViewModal;
